// components/tribute/TributeForm.tsx
"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { type Resolver, type SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

/* ===================== Zod schema & types ===================== */

const TributeSchema = z.object({
  title: z.string().min(1, "Title is required"),

  description: z.string().optional().or(z.literal("")),
  birthdate: z.string().optional().or(z.literal("")),
  deathdate: z.string().optional().or(z.literal("")),
  story: z.string().optional().or(z.literal("")),

  // Populated by upload route; hidden in UI
  photo_url: z.string().optional().or(z.literal("")),

  funeral_datetime: z.string().optional().or(z.literal("")),
  funeral_location: z.string().optional().or(z.literal("")),
  funeral_notes: z.string().optional().or(z.literal("")),

  // In-house RSVP (rsvps table)
  rsvp_enabled: z.boolean().default(false),

  privacy: z.enum(["public", "private"]).default("public"),

  // Optional external donation link (charity, memorial fund, etc.)
  donation_url: z.string().url("Please enter a valid URL").optional().or(z.literal("")),

  // Linked vendors (handled by server via tribute_vendors)
  funeral_home_vendor_id: z.string().optional().or(z.literal("")),
  florist_vendor_id: z.string().optional().or(z.literal("")),
});

export type TributeFormValues = z.output<typeof TributeSchema>;

interface TributeFormProps {
  initialData?: Partial<z.input<typeof TributeSchema>>;
  onSubmit: (data: TributeFormValues) => Promise<void> | void;
  submitLabel?: string;
}

/** Small type for vendor options coming from /api/tribute/vendor-options */
interface VendorOption {
  id: string;
  name: string;
  category: string | null;
  location: string | null;
}

/* =========================== Form ============================ */

export default function TributeForm({ initialData, onSubmit, submitLabel = "Save tribute" }: TributeFormProps) {
  const resolver = zodResolver(TributeSchema) as unknown as Resolver<TributeFormValues, undefined, TributeFormValues>;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<TributeFormValues, undefined, TributeFormValues>({
    resolver,
    defaultValues: {
      title: "",
      description: "",
      birthdate: "",
      deathdate: "",
      story: "",
      photo_url: "",
      funeral_datetime: "",
      funeral_location: "",
      funeral_notes: "",
      rsvp_enabled: false,
      privacy: "public",
      donation_url: "",
      funeral_home_vendor_id: "",
      florist_vendor_id: "",
      ...(initialData as Partial<TributeFormValues>),
    },
    mode: "onBlur",
  });

  const [photoPreview, setPhotoPreview] = useState<string | null>(
    (initialData as TributeFormValues | undefined)?.photo_url || null,
  );
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [vendorsError, setVendorsError] = useState<string | null>(null);

  const rsvpEnabled = watch("rsvp_enabled");

  /* ---- Sync initial data ---- */
  useEffect(() => {
    if (initialData) {
      reset((prev) => ({
        ...prev,
        ...(initialData as Partial<TributeFormValues>),
      }));

      const initialWithPhoto = initialData as TributeFormValues | undefined;
      if (initialWithPhoto?.photo_url) {
        setPhotoPreview(initialWithPhoto.photo_url || null);
      }
    }
  }, [initialData, reset]);

  /* ---- Load vendor options (booked vendors) ---- */
  useEffect(() => {
    let cancelled = false;

    async function loadVendors() {
      try {
        setVendorsLoading(true);
        setVendorsError(null);

        const res = await fetch("/api/tribute/vendor-options");
        if (!res.ok) {
          throw new Error("Unable to load vendors");
        }

        const payload = (await res.json()) as { vendors: VendorOption[] };
        if (!cancelled) {
          setVendors(payload.vendors ?? []);
        }
      } catch (_error) {
        if (!cancelled) {
          setVendorsError("We couldn’t load your vendors right now.");
        }
      } finally {
        if (!cancelled) setVendorsLoading(false);
      }
    }

    void loadVendors();

    return () => {
      cancelled = true;
    };
  }, []);

  /* ---- Image upload handler ---- */
  async function handlePhotoChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError(null);
    setUploadingPhoto(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch("/api/tribute/upload-photo", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const payload = await res.json().catch(() => null);
        const message = payload?.error ?? "We couldn’t upload that image. Please try again.";
        setUploadError(message);
        return;
      }

      const data = (await res.json()) as { url: string };
      setValue("photo_url", data.url, { shouldDirty: true });
      setPhotoPreview(data.url);
    } catch (_error) {
      setUploadError("Something went wrong while uploading. Please try again.");
    } finally {
      setUploadingPhoto(false);
    }
  }

  function handleRemovePhoto() {
    setPhotoPreview(null);
    setValue("photo_url", "", { shouldDirty: true });
  }

  const onValid: SubmitHandler<TributeFormValues> = (data) => onSubmit(data);

  return (
    <form
      onSubmit={handleSubmit(onValid)}
      className="mx-auto max-w-3xl space-y-8 rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-8"
      noValidate
    >
      {/* Header */}
      <header className="space-y-3">
        <p className="pill inline-flex text-[0.7rem] font-medium uppercase tracking-[0.18em] text-muted-foreground">
          Tribute
        </p>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">{submitLabel}</h1>
        <p className="max-w-2xl text-sm text-muted-foreground">
          Start with the basics: who this tribute is for, key dates, and a short description. You can add more details
          at any time.
        </p>
      </header>

      {/* Person details */}
      <section className="space-y-4">
        <h2 className="text-sm font-semibold tracking-tight text-foreground sm:text-base">About this person</h2>

        <div className="space-y-1">
          <label htmlFor="title" className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
            Name <span className="text-destructive">*</span>
          </label>
          <input
            id="title"
            {...register("title")}
            className="w-full rounded-2xl border border-border bg-background px-3 py-2 text-sm text-foreground outline-none ring-0 transition focus:border-primary focus:ring-2 focus:ring-primary/20"
            placeholder="For example: In memory of Jordan Taylor"
            required
          />
          {errors.title && <p className="mt-1 text-xs text-destructive">{errors.title.message}</p>}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <label
              htmlFor="birthdate"
              className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground"
            >
              Birth date
            </label>
            <input
              id="birthdate"
              type="date"
              {...register("birthdate")}
              className="w-full rounded-2xl border border-border bg-background px-3 py-2 text-sm text-foreground outline-none ring-0 transition focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
            {errors.birthdate && <p className="mt-1 text-xs text-destructive">{errors.birthdate.message}</p>}
          </div>

          <div className="space-y-1">
            <label
              htmlFor="deathdate"
              className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground"
            >
              Passing date
            </label>
            <input
              id="deathdate"
              type="date"
              {...register("deathdate")}
              className="w-full rounded-2xl border border-border bg-background px-3 py-2 text-sm text-foreground outline-none ring-0 transition focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
            {errors.deathdate && <p className="mt-1 text-xs text-destructive">{errors.deathdate.message}</p>}
          </div>
        </div>

        <div className="space-y-1">
          <label
            htmlFor="description"
            className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground"
          >
            Short tribute
          </label>
          <textarea
            id="description"
            {...register("description")}
            rows={3}
            className="w-full rounded-2xl border border-border bg-background px-3 py-2 text-sm text-foreground outline-none ring-0 transition focus:border-primary focus:ring-2 focus:ring-primary/20"
            placeholder="A short sentence or two that describes who they were."
          />
          {errors.description && <p className="mt-1 text-xs text-destructive">{errors.description.message}</p>}
        </div>

        <div className="space-y-1">
          <label htmlFor="story" className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
            Story / obituary
          </label>
          <textarea
            id="story"
            {...register("story")}
            rows={6}
            className="w-full rounded-2xl border border-border bg-background px-3 py-2 text-sm text-foreground outline-none ring-0 transition focus:border-primary focus:ring-2 focus:ring-primary/20"
            placeholder="Share their story, memories, or an obituary. You can paste in text and refine it later."
          />
          {errors.story && <p className="mt-1 text-xs text-destructive">{errors.story.message}</p>}
        </div>
      </section>

      {/* Photo upload */}
      <section className="space-y-3 border-t border-border pt-6">
        <h2 className="text-sm font-semibold tracking-tight text-foreground sm:text-base">Main photo</h2>
        <p className="text-xs text-muted-foreground">
          This will appear at the top of the tribute page. You can change it later.
        </p>

        {/* Hidden field for form state */}
        <input type="hidden" {...register("photo_url")} />

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <label className="flex-1 text-xs text-muted-foreground">
            <span className="mb-1 block text-[0.7rem] font-medium uppercase tracking-[0.16em]">Upload a photo</span>
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              className="block w-full text-xs text-muted-foreground file:mr-3 file:rounded-full file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-[0.7rem] file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
            />
          </label>

          {photoPreview && (
            <div className="flex flex-col items-start gap-2 sm:w-40">
              <div className="overflow-hidden rounded-2xl border border-border bg-muted/40">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={photoPreview} alt="Tribute preview" className="h-24 w-24 object-cover" />
              </div>
              <button type="button" onClick={handleRemovePhoto} className="btn btn-ghost px-2 py-1 text-[0.7rem]">
                Remove photo
              </button>
            </div>
          )}
        </div>

        {uploadingPhoto && <p className="text-[0.7rem] text-muted-foreground">Uploading image…</p>}
        {uploadError && <p className="text-[0.7rem] text-destructive">{uploadError}</p>}
      </section>

      {/* Service details + linked vendors */}
      <section className="space-y-6 border-t border-border pt-6">
        <div className="space-y-4">
          <h2 className="text-sm font-semibold tracking-tight text-foreground sm:text-base">
            Service &amp; gathering details
          </h2>

          <div className="space-y-1">
            <label
              htmlFor="funeral_datetime"
              className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground"
            >
              Funeral date &amp; time
            </label>
            <input
              id="funeral_datetime"
              type="datetime-local"
              {...register("funeral_datetime")}
              className="w-full rounded-2xl border border-border bg-background px-3 py-2 text-sm text-foreground outline-none ring-0 transition focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
            {errors.funeral_datetime && (
              <p className="mt-1 text-xs text-destructive">{errors.funeral_datetime.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <label
              htmlFor="funeral_location"
              className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground"
            >
              Location
            </label>
            <input
              id="funeral_location"
              {...register("funeral_location")}
              className="w-full rounded-2xl border border-border bg-background px-3 py-2 text-sm text-foreground outline-none ring-0 transition focus:border-primary focus:ring-2 focus:ring-primary/20"
              placeholder="Venue name, address, or general location."
            />
            {errors.funeral_location && (
              <p className="mt-1 text-xs text-destructive">{errors.funeral_location.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <label
              htmlFor="funeral_notes"
              className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground"
            >
              Additional notes
            </label>
            <textarea
              id="funeral_notes"
              {...register("funeral_notes")}
              rows={3}
              className="w-full rounded-2xl border border-border bg-background px-3 py-2 text-sm text-foreground outline-none ring-0 transition focus:border-primary focus:ring-2 focus:ring-primary/20"
              placeholder="Dress code, parking details, virtual link, or anything guests should know."
            />
            {errors.funeral_notes && <p className="mt-1 text-xs text-destructive">{errors.funeral_notes.message}</p>}
          </div>
        </div>

        {/* Linked vendors */}
        <div className="space-y-3 rounded-2xl border border-border bg-muted/30 p-4">
          <h3 className="text-sm font-semibold tracking-tight text-foreground">Linked vendors</h3>
          <p className="text-xs text-muted-foreground">
            If you’ve already booked a funeral home or florist through Starlit Passage, you can link them here so guests
            know where services are being held and where to send flowers.
          </p>

          {vendorsError && <p className="text-[0.7rem] text-destructive">{vendorsError}</p>}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label
                htmlFor="funeral_home_vendor_id"
                className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground"
              >
                Funeral home or venue
              </label>
              <select
                id="funeral_home_vendor_id"
                {...register("funeral_home_vendor_id")}
                disabled={vendorsLoading || vendors.length === 0}
                className="w-full rounded-2xl border border-border bg-background px-3 py-2 text-sm text-foreground outline-none ring-0 transition disabled:cursor-not-allowed disabled:bg-muted focus:border-primary focus:ring-2 focus:ring-primary/20"
              >
                <option value="">
                  {vendorsLoading
                    ? "Loading vendors…"
                    : vendors.length === 0
                      ? "No booked vendors yet"
                      : "Choose a vendor (optional)"}
                </option>
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                    {v.location ? ` — ${v.location}` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label
                htmlFor="florist_vendor_id"
                className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground"
              >
                Florist (optional)
              </label>
              <select
                id="florist_vendor_id"
                {...register("florist_vendor_id")}
                disabled={vendorsLoading || vendors.length === 0}
                className="w-full rounded-2xl border border-border bg-background px-3 py-2 text-sm text-foreground outline-none ring-0 transition disabled:cursor-not-allowed disabled:bg-muted focus:border-primary focus:ring-2 focus:ring-primary/20"
              >
                <option value="">
                  {vendorsLoading
                    ? "Loading vendors…"
                    : vendors.length === 0
                      ? "No booked vendors yet"
                      : "Choose a florist (optional)"}
                </option>
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                    {v.location ? ` — ${v.location}` : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Visibility / RSVP / donations */}
      <section className="space-y-4 border-t border-border pt-6">
        <h2 className="text-sm font-semibold tracking-tight text-foreground sm:text-base">
          Visibility, RSVP &amp; donations
        </h2>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <label htmlFor="privacy" className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
              Privacy
            </label>
            <select
              id="privacy"
              {...register("privacy")}
              className="w-full rounded-2xl border border-border bg-background px-3 py-2 text-sm text-foreground outline-none ring-0 transition focus:border-primary focus:ring-2 focus:ring-primary/20"
            >
              <option value="public">Public (searchable link)</option>
              <option value="private">Private (link-only)</option>
            </select>
            {errors.privacy && <p className="mt-1 text-xs text-destructive">{errors.privacy.message}</p>}
          </div>

          <div className="space-y-2 rounded-2xl border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
            <label className="inline-flex items-center gap-2 text-xs text-foreground">
              <input
                type="checkbox"
                {...register("rsvp_enabled")}
                className="h-4 w-4 rounded border-border text-primary outline-none ring-0 focus-visible:ring-2 focus-visible:ring-primary/40"
              />
              Enable RSVP on this tribute
            </label>
            <p className="mt-1">When enabled, guests can respond directly on the tribute page.</p>
          </div>
        </div>

        {rsvpEnabled && (
          <p className="text-[0.7rem] text-muted-foreground">
            RSVPs will be managed inside Starlit Passage. You’ll see responses under the tribute and in your dashboard.
          </p>
        )}

        <div className="space-y-1">
          <label
            htmlFor="donation_url"
            className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground"
          >
            Donation link (optional)
          </label>
          <input
            id="donation_url"
            {...register("donation_url")}
            placeholder="https://…"
            className="w-full rounded-2xl border border-border bg-background px-3 py-2 text-sm text-foreground outline-none ring-0 transition focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
          <p className="mt-1 text-[0.7rem] text-muted-foreground">
            For example, a charity or memorial fund chosen by the family. Direct contributions through Starlit Passage
            will come later.
          </p>
          {errors.donation_url && <p className="mt-1 text-xs text-destructive">{errors.donation_url.message}</p>}
        </div>
      </section>

      {/* Actions */}
      <div className="flex justify-end pt-2">
        <button type="submit" disabled={isSubmitting} className="btn btn-primary min-w-[8rem]">
          {isSubmitting ? "Saving…" : submitLabel}
        </button>
      </div>
    </form>
  );
}
